import React from "react";
import styled from "styled-components";
import { Link, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { useAuth } from "../../contexts/AuthContext";
import MoreActionsMenu from "../common/MoreActionsMenu";

const Card = styled.article`
  padding: 2.5rem 0;
  padding-left: 1.5rem;
  padding-right: 1.5rem;
  border-bottom: 1px solid var(--color-border);
  position: relative;
  transition: all 0.2s ease;
  display: flex;
  gap: 2rem;
  align-items: flex-start;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1.5rem;
  }

  &:last-child {
    border-bottom: none;
  }

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: transparent;
    transition: background 0.2s ease;
  }

  &:hover {
    background: rgba(0, 0, 0, 0.01);

    &::before {
      background: var(--color-accent);
    }
  }
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 0.75rem;
  gap: 1rem;
`;

const Title = styled.h2`
  font-size: 2rem;
  margin: 0;
  flex: 1;

  a {
    color: var(--color-text);
    text-decoration: none;

    &:hover {
      color: var(--color-accent);
      text-decoration: none;
    }
  }
`;

const Meta = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  margin-bottom: 1rem;
`;

const PostDate = styled.time`
  font-size: 0.875rem;
  color: var(--color-text-light);
`;

const Tag = styled.span`
  font-size: 0.875rem;
  color: var(--color-accent);
  font-weight: 500;

  &:before {
    content: "•";
    margin-right: 1rem;
    color: var(--color-text-light);
  }
`;

const Excerpt = styled.p`
  font-size: 1.05rem;
  line-height: 1.7;
  color: var(--color-text-light);
  margin-bottom: 1rem;
`;

const ReadMore = styled(Link)`
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;

  &:after {
    content: "→";
    transition: transform 0.2s ease;
  }

  &:hover:after {
    transform: translateX(4px);
  }
`;

const ThumbnailContainer = styled(Link)`
  flex-shrink: 0;
  width: 200px;
  height: 150px;
  overflow: hidden;
  border-radius: 8px;
  background: var(--color-bg);
  position: relative;

  @media (max-width: 768px) {
    width: 100%;
    height: 200px;
  }
`;

const Thumbnail = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;

  ${ThumbnailContainer}:hover & {
    transform: scale(1.05);
  }
`;

const ContentWrapper = styled.div`
  flex: 1;
  min-width: 0;
`;

const BlogCard = ({ post }) => {
  const { currentUser, isAuthorized } = useAuth();
  const navigate = useNavigate();
  const formattedDate = post.createdAt
    ? format(
        post.createdAt.toDate
          ? post.createdAt.toDate()
          : new Date(post.createdAt),
        "MMMM d, yyyy"
      )
    : "";

  // Allow any authorized admin to edit posts
  const canEdit = isAuthorized;

  // Get thumbnail URL (no transformations needed)
  const thumbnailUrl = post.thumbnail || null;

  return (
    <Card>
      {thumbnailUrl && (
        <ThumbnailContainer to={`/blog/${post.id}`}>
          <Thumbnail src={thumbnailUrl} alt={post.title} loading="lazy" />
        </ThumbnailContainer>
      )}
      <ContentWrapper>
        <Header>
          <Title>
            <Link to={`/blog/${post.id}`}>{post.title}</Link>
          </Title>
          {canEdit && (
            <MoreActionsMenu
              item={post}
              collection="posts"
              onEdit={() => navigate(`/admin/edit/${post.id}`)}
            />
          )}
        </Header>
        <Meta>
          <PostDate>{formattedDate}</PostDate>
          {post.category && <Tag>{post.category}</Tag>}
        </Meta>
        <Excerpt>{post.excerpt}</Excerpt>
        <ReadMore to={`/blog/${post.id}`}>Read more</ReadMore>
      </ContentWrapper>
    </Card>
  );
};

export default BlogCard;
